import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { authApi } from './api';
import { Toaster } from 'sonner';
import {
  LayoutDashboard, Users, Send, BarChart3, ShieldBan, Settings, LogOut, Mail,
  Inbox as InboxIcon, PenSquare, Search, ChevronDown
} from 'lucide-react';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import Campaigns from './pages/Campaigns';
import CampaignCreate from './pages/CampaignCreate';
import CampaignDetail from './pages/CampaignDetail';
import Analytics from './pages/Analytics';
import Blacklist from './pages/Blacklist';
import SettingsPage from './pages/Settings';
import InboxPage from './pages/Inbox';

const NAV = [
  { to: '/outreach', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/outreach/leads', icon: Users, label: 'Leads' },
  { to: '/outreach/campaigns', icon: Send, label: 'Campaigns' },
  { to: '/outreach/inbox', icon: InboxIcon, label: 'Inbox' },
  { to: '/outreach/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/outreach/blacklist', icon: ShieldBan, label: 'Blacklist' },
  { to: '/outreach/settings', icon: Settings, label: 'Settings' },
];

function Sidebar() {
  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.removeItem('outreach_token');
    navigate('/outreach/login');
  };

  return (
    <aside className="w-[260px] bg-[#F8F9FA] flex flex-col h-screen fixed left-0 top-0 z-40" data-testid="outreach-sidebar">
      {/* Logo */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#1A73E8] rounded-xl flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[15px] font-bold text-[#202124] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>StartEase</div>
            <div className="text-[10px] text-[#5F6368] uppercase tracking-widest font-semibold">Outreach</div>
          </div>
        </div>
      </div>

      {/* Compose Button */}
      <div className="px-3 mb-2">
        <button
          onClick={() => navigate('/outreach/campaigns/new')}
          className="flex items-center gap-2.5 w-full px-5 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg text-[#202124] font-medium text-sm transition-shadow border border-[#E2E8F0]"
          data-testid="compose-campaign-btn"
        >
          <PenSquare className="w-5 h-5 text-[#1A73E8]" />
          <span>New Campaign</span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to} to={to} end={end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                isActive
                  ? 'bg-[#D3E3FD] text-[#041E49] font-semibold'
                  : 'text-[#5F6368] hover:bg-[#E8EAED]'
              }`
            }
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon className="w-[18px] h-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="px-2 pb-4">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2 rounded-full text-[13px] font-medium text-[#5F6368] hover:bg-[#FCE8E6] hover:text-[#D93025] w-full transition-colors"
          data-testid="nav-logout"
        >
          <LogOut className="w-[18px] h-[18px]" /> Logout
        </button>
      </div>
    </aside>
  );
}

function ProtectedLayout() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('outreach_token');
    if (!token) { setAuthed(false); return; }
    authApi.me().then(() => setAuthed(true)).catch(() => { setAuthed(false); localStorage.removeItem('outreach_token'); });
  }, []);

  if (authed === null) return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!authed) return <Navigate to="/outreach/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" style={{ fontFamily: 'Manrope, Inter, sans-serif' }}>
      <Sidebar />
      <main className="ml-[260px] min-h-screen">
        <div className="p-6">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/new" element={<CampaignCreate />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="blacklist" element={<Blacklist />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function OutreachApp() {
  return (
    <>
      <Toaster position="bottom-left" richColors />
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="*" element={<ProtectedLayout />} />
      </Routes>
    </>
  );
}
